import { SignUpConfirmationLayout } from '../SignUpConfirmationLayout/SignUpConfirmationLayout.component';
import { SignUpForm } from '../SignUpForm/SignUpForm.component';
import { RegistrationProcess, Resend } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { SignUpProfile } from '@auth/business/auth.utils';
import { useAuthModal } from '@auth/business/AuthModal.hook';
import { UnderlinedButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { usePlcRouter } from '@utils/customHooks';
import { handleScrollTop } from '@utils/navigation';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

type Props = ComponentPropsWithRef<'main'>;

const SignUpLayout: FC<Props> = ({ className, ...rest }) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const [confirmationMode, setConfirmationMode] = useState<Resend | undefined>();
  const { showSignInModal } = useAuthModal();
  const { push } = usePlcRouter();

  useEffect(() => (timeoutRef.current ? clearTimeout(timeoutRef.current) : undefined), [timeoutRef]);

  function handleDisplayConfirmation(resend: Resend) {
    setConfirmationMode(resend);
    handleScrollTop();
  }

  function handleNewMemberProfile() {
    setConfirmationMode(undefined);
    timeoutRef.current = setTimeout(() => document.getElementById('sign-up-member-email-input')?.focus(), 100);
  }

  const rootClass = classnames('sign-up-layout', 'plc-page', className);
  const sectionClassName = classnames(!isMobile && 'wrapper-s', 'flex', 'main-section', 'plc-mb-l');

  const tagProps: PlcTagProps = useMemo(
    () => ({
      level2: ATLevel2.SignUp,
      tagPageNameI18nArray: ['auth.sign-up.tag.title'],
    }),
    []
  );

  if (confirmationMode) {
    return (
      <SignUpConfirmationLayout
        isMobile={isMobile}
        onNewMemberProfileClick={handleNewMemberProfile}
        resend={confirmationMode}
      />
    );
  }

  function onContactClick() {
    push(urlEnum.contactUs);
  }

  return (
    <PlcPageLayout className={rootClass} metaTitleI18n="auth.sign-up.tag.title" tagProps={tagProps} {...rest}>
      <PlcPageHeader className="plc-mb-l" heroImgSrc="inscription" i18nTitle="auth.sign-up.title" />
      <section className={sectionClassName} id="sign-up-form-section">
        <SignUpForm
          active={true}
          onSignUpComplete={handleDisplayConfirmation}
          profile={SignUpProfile.member}
          registrationProcess={RegistrationProcess.RegularCac}
        />
      </section>

      <section className="wrapper-s main-section plc-mb-l">
        <Text i18nKey="auth.is-signed-up" tag="span" variant="small" />
        <UnderlinedButton
          onClick={() => showSignInModal()}
          textProps={{ flavour: 'red', i18nKey: 'auth.sign-in.your-account', variant: 'small' }}
        />
        <Text className="plc-mt-m" i18nKey="auth.issue.difficulties" variant="small">
          <Trans i18nKey="auth.issue.contact-us.body" ns="auth">
            Contact us
            <UnderlinedButton onClick={onContactClick} textProps={{ tag: 'span', variant: 'small' }}>
              contact form
            </UnderlinedButton>
          </Trans>
        </Text>
      </section>
    </PlcPageLayout>
  );
};

export { SignUpLayout };
